import { setAuthToken, setStoredUser } from '@/utils/auth';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, AlertCircle, Lock, Shield } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PartnerSetupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    password: '',
    confirm_password: '',
    accept_terms: false
  });

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing setup token. Please use the link from your approval email.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (formData.password !== formData.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!formData.accept_terms) {
      setError('You must accept the terms and conditions');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/partner/setup-account`, {
        token,
        password: formData.password,
        confirm_password: formData.confirm_password,
        accept_terms: formData.accept_terms
      });

      // Store token and user data
      setAuthToken(res.data.token);
      setStoredUser(res.data.user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;

      setSuccess(true);
      toast.success('Account created successfully!');

      // Redirect after 2 seconds
      setTimeout(() => {
        const role = res.data.user.role;
        const dashboardRoutes = {
          'developer': '/partner/dashboard/developer',
          'broker': '/partner/dashboard/broker',
          'agent': '/partner/dashboard/agent',
          'car_dealer': '/partner/dashboard/cars',
          'yacht_dealer': '/partner/dashboard/yachts',
          'interior_designer': '/partner/dashboard/design',
          'other_business': '/partner/dashboard/services'
        };
        navigate(dashboardRoutes[role] || '/partner/dashboard');
      }, 2000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create account');
      toast.error('Account setup failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-obsidian text-text-primary flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card p-12 text-center max-w-md"
        >
          <CheckCircle className="w-20 h-20 text-gold mx-auto mb-6" />
          <h2 className="font-playfair text-3xl font-semibold mb-4">Account Created!</h2>
          <p className="text-text-secondary mb-4">Your partner account has been set up successfully.</p>
          <p className="text-text-secondary text-sm">Redirecting to your dashboard...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary flex items-center justify-center p-4">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Link to="/" className="font-playfair text-3xl font-semibold text-gold">ASTIIV BASGAT</Link>
            <h2 className="text-xl text-text-secondary mt-2">Create Your Partner Account</h2>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-3 p-4 rounded-lg border bg-red-500/20 text-red-400 border-red-500/30 mb-6"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </motion.div>
          )}

          {!token ? (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
              <p className="text-text-secondary mb-4">Invalid setup link</p>
              <Button onClick={() => navigate('/partner/login')} className="bg-gold text-black">
                Go to Partner Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label className="text-text-primary flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Create Password
                </Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-white text-black mt-1"
                  placeholder="Min 8 characters"
                  required
                  minLength={8}
                />
              </div>

              <div>
                <Label className="text-text-primary flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Confirm Password
                </Label>
                <Input
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  className="bg-white text-black mt-1"
                  placeholder="Re-enter password"
                  required
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={formData.accept_terms}
                  onChange={(e) => setFormData({ ...formData, accept_terms: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded border-white/20"
                />
                <label htmlFor="terms" className="text-sm text-text-secondary">
                  I accept the{' '}
                  <Link to="/legal/terms" target="_blank" rel="noopener" className="text-gold hover:underline" data-testid="setup-tnc-link">Terms & Conditions</Link>
                  ,{' '}
                  <Link to="/legal/privacy" target="_blank" rel="noopener" className="text-gold hover:underline" data-testid="setup-privacy-link">Privacy Policy</Link>
                  {' '}and{' '}
                  <Link to="/legal/partner-agreement" target="_blank" rel="noopener" className="text-gold hover:underline" data-testid="setup-partner-agreement-link">Partner Agreement</Link>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-black hover:bg-gold-light py-6"
                data-testid="create-account-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PartnerSetupPage;
